/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {HttpResponse} from '@angular/common/http';
import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {FabMessageService} from '@fabric/components/message';
import {
  GridApi,
  GridReadyEvent,
  PostSortRowsParams,
  RowSelectionOptions,
  SelectionChangedEvent,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG, FCORG_BLACKLIST, VEHICLE_PROPULSION_COLDEF, VFUEL_RECORDS_EXCLUDED_HEADER, VFUEL_RECORDS_EXCLUDED_MSG} from '../../../common/constants';
import {getDefaultYearRange, sortVehicleScenarios} from '../../../core/helpers/generic-helpers';
import {StateService} from '../../../core/state/state.service';
import {VehicleScenario} from '../../../models/vehicle-scenario-dto.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';
import {VehicleScenarioService} from '../../../services/vehicle-scenario.service';

@Component({
  selector: 'app-prgm-code-by-fuel-type-share-rpt',
  templateUrl: './prgm-code-by-fuel-type-share-rpt.component.html',
  styleUrl: './prgm-code-by-fuel-type-share-rpt.component.scss',
  standalone: false
})
export class PrgmCodeByFuelTypeShareRptComponent implements OnInit {

  protected readonly VEHICLE_PROPULSION_COLDEF = VEHICLE_PROPULSION_COLDEF;

  showSpinner = false;
  availableScenarios: VehicleScenario[] = [];
  selectedScenario: VehicleScenario;
  gridApi: GridApi;

  yearRange: number[];
  startYear: number;
  endYear: number;

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: 'fitGridWidth',
  };

  rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  };

  postSortRows: (params: PostSortRowsParams) => void = (
    params: PostSortRowsParams<VehicleScenario>,
  ) => {
    if (this.stateService.userPreferences?.some(pref => pref.preferenceKey === 'Primary FCORG')) {
      const rowNodes = params.nodes;
      let nextInsertPos = 0;
      for (let i = 0; i < rowNodes.length; i++) {
        const fcorg = rowNodes[i].data ? rowNodes[i].data!.fcorg : undefined;
        if (fcorg === this.stateService.userPreferences.find(pref => pref.preferenceKey === 'Primary FCORG')?.preferenceValue) {
          rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
          nextInsertPos++;
        }
      }
    }
  };

  constructor(public snackBar: FabMessageService,
              public readonly excelService: ExcelExportService,
              readonly vehicleScenarioService: VehicleScenarioService,
              readonly propulsionScenarioService: PropulsionScenarioService,
              readonly stateService: StateService,
              private readonly changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.getYearRange();
    this.getVehicleScenarios();
  }

  /**
   * Gets the list of vehicle scenarios to populate the selection table
   */
  getVehicleScenarios(): void {
    this.showSpinner = true;
    this.vehicleScenarioService.getVehicleScenariosForSelect(this.stateService.getUserGmid(), false).subscribe({
      next: (data) => {
        this.availableScenarios = data
        .filter(row => !FCORG_BLACKLIST.includes(row.fcorg))
        .filter(row => row.propulsionScenario?.id != null);
        this.availableScenarios = sortVehicleScenarios(this.availableScenarios)
        .filter(row => !row.fcorg.includes('TVP'));
      },
      error: (err) => {
        this.showSpinner = false;
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
      }
    });
  }

  /**
   * Gets the default year range for the report
   */
  getYearRange(): void {
    const {yearRange, startYear, endYear} = getDefaultYearRange();
    this.yearRange = yearRange;
    this.startYear = startYear;
    this.endYear = endYear;
  }

  /**
   * Updates the selected vehicle scenario when grid selection changes
   * @param event the grid selection changed event
   */
  onSelectionChanged(event: SelectionChangedEvent): void {
    const selectedRows = event.api.getSelectedRows() as VehicleScenario[];
    this.selectedScenario = selectedRows.length > 0 ? selectedRows[0] : undefined;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Whether the run report button should be disabled
   */
  isRunReportDisabled(): boolean {
    return !this.selectedScenario || !this.startYear || !this.endYear;
  }

  /**
   * Runs the report and exports the results to Excel
   */
  onRunReport(): void {
    if (!this.selectedScenario?.propulsionScenario?.id) {
      this.snackBar.displayError('Please select a vehicle scenario with a propulsion scenario', DISMISS_MSG);
      return;
    }

    this.showSpinner = true;
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const fileName = 'PrgmCodeFuelTypeShare_' + this.selectedScenario.vehScenId + '_' + currDate;
    this.propulsionScenarioService.getPrgmCodeByFuelTypeShareReport(
      this.stateService.getUserGmid(),
      this.selectedScenario.vehScenId,
      this.startYear,
      this.endYear
    ).subscribe({
      next: (response: HttpResponse<Blob>) => {
        this.excelService.exportToExcelFromServer(response.body, fileName);
        if (response.headers.get(VFUEL_RECORDS_EXCLUDED_HEADER) === 'true') {
          this.snackBar.displayError(VFUEL_RECORDS_EXCLUDED_MSG, DISMISS_MSG);
        } else {
          this.snackBar.displaySuccess('Downloaded Successfully', DISMISS_MSG);
        }
      },
      error: (error: any) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
      }
    });
  }

  /**
   * Handler for when the grid is ready
   * @param params the grid ready event parameters
   */
  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
  }
}
